import { yupResolver } from '@hookform/resolvers/yup';
import type { FC } from 'react';
import { useEffect, useCallback } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import { useQueryClient } from 'react-query';

import { Divider, GoBack, useAuthContext } from '@catalyze/ui';
import { useUpdateUserMutation } from 'api/users/updateUser';
import { BrokerageDetails } from 'features/Profile/components/BrokerageDetails';
import { PasswordChange } from 'features/Profile/components/PasswordChange';
import { ProfileDetails } from 'features/Profile/components/ProfileDetails';
import { ProfileSection } from 'features/Profile/components/ProfileSection';
import { Subscription } from 'features/Profile/components/Subscription';
import { Toast } from 'features/Profile/components/Toast';
import { userProfileSchema } from 'schemas/userProfile';
import { getFormDirtyValues } from 'utils/getFormDirtyValues';

import { ProfileContainer, ProfileSectionContainer } from './styled';

export type TFormInputs = {
    firstName: string;
    lastName: string;
    age: number;
    gender: string;
    email: string;
    brokerageLicenseNumber: string;
    brokerageName: string;
    zip: string;
    newPassword: string;
    currentPassword: string;
};

export const Profile: FC = () => {
    const queryClient = useQueryClient();
    const { user, handleUserSignIn } = useAuthContext();
    const {
        firstName,
        lastName,
        age,
        gender,
        email,
        brokerageLicenseNumber,
        brokerageName,
        subscription,
        zip,
    } = user || {};
    const formMethods = useForm<TFormInputs>({
        resolver: yupResolver(userProfileSchema),
        defaultValues: {
            firstName,
            lastName,
            age,
            gender,
            email,
            brokerageLicenseNumber,
            brokerageName,
            zip,
            newPassword: '',
            currentPassword: '',
        },
    });
    const { reset, getValues, handleSubmit, formState, setError } = formMethods;
    const { isDirty, dirtyFields } = formState;

    const { mutate: updateUser } = useUpdateUserMutation({
        onSuccess: async () => {
            await queryClient.invalidateQueries(['user']);
            toast.dismiss();
        },
    });

    const handleCancelChanges = useCallback(() => {
        reset();
        toast.dismiss();
    }, [reset]);

    const handleSaveChanges = useCallback(async () => {
        const dirtyValues = getFormDirtyValues(dirtyFields, getValues());

        // if the password fields are not changed, we don't want to include them in the request
        if (dirtyValues.currentPassword && dirtyValues.newPassword && email) {
            dirtyValues.password = dirtyValues.newPassword;

            // API expects just one password field instead of 2
            delete dirtyValues.currentPassword;
            delete dirtyValues.newPassword;

            try {
                await handleUserSignIn({
                    email,
                    password: getValues('currentPassword'),
                });
                updateUser({
                    data: dirtyValues,
                });
            } catch {
                setError('currentPassword', {
                    message: 'Wrong current password.',
                });
            }
        } else {
            updateUser({
                data: dirtyValues,
            });
        }
    }, [dirtyFields, updateUser, email, handleUserSignIn, getValues, setError]);

    useEffect(() => {
        if (isDirty) {
            toast(
                () => (
                    <Toast
                        onCancel={handleCancelChanges}
                        onSave={handleSubmit(handleSaveChanges)}
                    />
                ),
                {
                    style: { width: '70rem', maxWidth: '75%' }, // these style has to be set on the wrapper component
                    id: 'unsaved-changes', // id prevent from rendering more than one toast
                    duration: Infinity,
                    position: 'bottom-right',
                }
            );
        }
    }, [isDirty, handleCancelChanges, handleSaveChanges, handleSubmit]);

    return (
        <ProfileContainer>
            <nav>
                <GoBack />
            </nav>
            <FormProvider {...formMethods}>
                <form>
                    <ProfileSectionContainer>
                        <ProfileSection
                            title="Details"
                            description="Basic information about you."
                        >
                            <ProfileDetails />
                        </ProfileSection>
                        <Divider />

                        <ProfileSection
                            title="Brokerage license"
                            description="Your broker license informations."
                        >
                            <BrokerageDetails
                                zip={zip}
                                formMethods={formMethods}
                            />
                        </ProfileSection>
                        <Divider />
                        <ProfileSection
                            title="Password"
                            description="Change your password."
                        >
                            <PasswordChange />
                        </ProfileSection>

                        <Divider />
                        <ProfileSection
                            title="Subscription"
                            description="Manage your subscription."
                        >
                            <Subscription plan={subscription} />
                        </ProfileSection>
                    </ProfileSectionContainer>
                </form>
            </FormProvider>
        </ProfileContainer>
    );
};
